import React, { Suspense, useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, BellOff, LayoutDashboard, ShoppingBag, Warehouse, Tags, Users, Activity, Settings, LogOut, Menu, Search, Sparkles,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrderAlerts } from '../utils/orderAlerts';
import { useCapabilities } from '../capabilities';
import { Avatar, FindAsk, LoadingState, Logo, TrialBanner, Wordmark, Modal, useToast } from '../components';

const isTypingTarget = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

const WORK_LINKS = [
  { to: '/', label: 'Dashboard', shortLabel: 'Home', icon: LayoutDashboard, end: true },
  { to: '/orders', label: 'Orders', icon: ShoppingBag, showCount: true },
  { to: '/storage', label: 'Storage', icon: Warehouse },
  { to: '/skus', label: 'SKUs', icon: Tags },
];

const ADMIN_LINKS = [
  { to: '/users', label: 'Users', icon: Users },
  { to: '/activity', label: 'Activity', icon: Activity },
  { to: '/settings', label: 'Settings', icon: Settings },
];

function AdminLayout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  // Orders waiting to be picked (badge on Orders), and an alert when new ones arrive
  const { enabled: alertsOn, toggle: toggleAlerts, activeCount } = useOrderAlerts((fresh) => {
    toast.success(fresh.length === 1 ? `New order #${fresh[0].order_number}` : `${fresh.length} new orders`);
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [findOpen, setFindOpen] = useState(false);
  const { aiEnabled } = useCapabilities();
  const findLabel = aiEnabled ? 'Find or ask…' : 'Find stock…';
  const business = user?.business;
  const creditsLabel = business?.unlimited_credits ? 'Unlimited' : (business?.credits ?? 0).toLocaleString();
  const lowCredits = business && !business.unlimited_credits && business.credits <= 3;

  // Ctrl/Cmd+K anywhere, or "/" when not typing, opens Find & Ask
  useEffect(() => {
    const onKeyDown = (event) => {
      const shortcut = (event.key === 'k' && (event.metaKey || event.ctrlKey))
        || (event.key === '/' && !isTypingTarget(event.target));
      if (shortcut) {
        event.preventDefault();
        setFindOpen(true);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const sidebarLink = ({ to, label, icon: Icon, end, showCount }) => (
    <NavLink key={to} to={to} end={end} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
      <Icon size={18} aria-hidden="true" />
      {label}
      {showCount && activeCount > 0 && <span className="sidebar-link-badge" aria-label={`${activeCount} to pick`}>{activeCount}</span>}
    </NavLink>
  );

  return (
    <div className="shell">
      <aside className="sidebar">
        <Link to="/" className="brand">
          <Logo />
          <Wordmark />
        </Link>
        {business && <div className="sidebar-business" title={business.name}>{business.name}</div>}

        <button type="button" className="findask-trigger" onClick={() => setFindOpen(true)}>
          <Search size={16} aria-hidden="true" />
          {findLabel}
          <kbd>Ctrl K</kbd>
        </button>

        <nav className="sidebar-nav" aria-label="Main">
          {WORK_LINKS.map(sidebarLink)}
          {isAdmin && (
            <>
              <div className="sidebar-section-label">Admin</div>
              {ADMIN_LINKS.map(sidebarLink)}
            </>
          )}
        </nav>

        {aiEnabled && (
          <NavLink to="/credits" className={({ isActive }) => `sidebar-credits ${isActive ? 'active' : ''} ${lowCredits ? 'is-low' : ''}`}>
            <Sparkles size={16} aria-hidden="true" />
            <span>Plan & credits</span>
            <strong>{creditsLabel}</strong>
          </NavLink>
        )}

        <div className="sidebar-footer">
          <Avatar email={user?.email} />
          <div className="sidebar-user">
            <div className="sidebar-user-email" title={user?.email}>{user?.email}</div>
            <div className="sidebar-user-role">{user?.role}</div>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-icon btn-sm"
            onClick={toggleAlerts}
            aria-pressed={alertsOn}
            aria-label={alertsOn ? 'Turn off new-order alerts' : 'Turn on new-order alerts'}
            title={alertsOn ? 'New-order alerts on' : 'New-order alerts off'}
          >
            {alertsOn ? <Bell size={16} aria-hidden="true" /> : <BellOff size={16} aria-hidden="true" />}
          </button>
          <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={handleLogout} aria-label="Sign out" title="Sign out">
            <LogOut size={16} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <header className="topbar">
        <Link to="/" className="brand">
          <Logo size={26} />
          <Wordmark />
        </Link>
        <div className="row" style={{ gap: 8 }}>
          <button type="button" className="btn btn-ghost btn-icon" onClick={() => setFindOpen(true)} aria-label={findLabel}>
            <Search size={20} aria-hidden="true" />
          </button>
          <Avatar email={user?.email} />
        </div>
      </header>

      <main className="shell-main">
        {/* A page still downloading shows a spinner here, keeping the sidebar */}
        <TrialBanner />
        <Suspense fallback={<LoadingState />}>
          <Outlet />
        </Suspense>
      </main>

      <nav className="tabbar" aria-label="Main">
        {WORK_LINKS.map(({ to, label, shortLabel, icon: Icon, end, showCount }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `tabbar-link ${isActive ? 'active' : ''}`}>
            <Icon size={21} aria-hidden="true" />
            {shortLabel || label}
            {showCount && activeCount > 0 && <span className="tabbar-badge">{activeCount}</span>}
          </NavLink>
        ))}
        <button type="button" className={`tabbar-link ${menuOpen ? 'active' : ''}`} onClick={() => setMenuOpen(true)}>
          <Menu size={21} aria-hidden="true" />
          More
        </button>
      </nav>

      <FindAsk open={findOpen} onClose={() => setFindOpen(false)} />

      <Modal open={menuOpen} onClose={() => setMenuOpen(false)} title={user?.email} description={`${business?.name ?? ''} · ${user?.role}`}>
        <div className="menu-list">
          {aiEnabled && (
            <Link to="/credits" className="menu-item">
              <Sparkles size={20} aria-hidden="true" />
              Plan & credits
              <span className="menu-item-meta">{creditsLabel}</span>
            </Link>
          )}
          {isAdmin && ADMIN_LINKS.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to} className="menu-item">
              <Icon size={20} aria-hidden="true" />
              {label}
            </Link>
          ))}
          <button type="button" className="menu-item" onClick={handleLogout}>
            <LogOut size={20} aria-hidden="true" />
            Sign out
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default AdminLayout;
